/**
 * Step 9 — Day 1 streak intro — SYNTHESIS variant
 *
 * Spec: specs/onboarding-rebuild.md → Act 2 step 9
 *
 * Synthesizes:
 *   - Experimental's CSS-painted candle scene + ignition ceremony
 *     (wick → spark → flame with bounce → halo + flicker). Distinctive
 *     and ownable — emoji flames are everywhere; a painted candle reads
 *     as Illuminate's own visual.
 *   - Professional's SLOTS cascade pattern wrapped around the candle so
 *     the content (heading, verse, rest-promise, CTA) reveals on a shared
 *     exponential ease-out curve at 90ms stagger.
 *   - Professional's italic "Miss a day? Your streak doesn't break. Rest
 *     is biblical." copy (no emoji, italic-serif, tender).
 *
 * What synthesis explicitly drops:
 *   - Emoji flame (🔥) — too quotidian for a sermonic beat.
 *   - "Step 9 of 18" eyebrow.
 *   - Heading mutation language; the candle does the visual work.
 */

import { useEffect, useState } from 'react'
import ContinueButton from '@/components/onboarding/ContinueButton'

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'
const STAGGER_MS = 90

const SLOTS = {
  eyebrow: 0,
  candle: 1, // scene reveals here; the spark+flame stages drive its own keyframes
  heading: 2,
  verse: 3,
  restPromise: 4,
  cta: 5,
}

function fadeStyle(slot, mounted) {
  return {
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(8px)',
    transition: `opacity 720ms ${EASE} ${slot * STAGGER_MS + 80}ms, transform 720ms ${EASE} ${slot * STAGGER_MS + 80}ms`,
    willChange: 'opacity, transform',
  }
}

export default function StreakIntroSynthesis({ displayName, firstName, onContinue }) {
  const name = (firstName && firstName.trim()) || (displayName && displayName.trim()) || 'friend'
  const [mounted, setMounted] = useState(false)
  // Candle ceremony stages — ported from experimental variant:
  //   0 = unlit wick
  //   1 = spark briefly flashes on wick
  //   2 = flame ignites with bounce + halo fades in
  //   3 = settled flicker + halo pulse
  const [candleStage, setCandleStage] = useState(0)

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(id)
  }, [])

  // Stage the candle ignition once mounted. Same timing as experimental:
  // spark at +350ms after candle slot enters, ignite at +750ms, lit at +1300ms.
  // Respects prefers-reduced-motion by skipping straight to "lit".
  useEffect(() => {
    if (!mounted) return undefined
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) {
      setCandleStage(3)
      return undefined
    }
    const candleEntersAt = SLOTS.candle * STAGGER_MS + 80
    const timers = [
      setTimeout(() => setCandleStage(1), candleEntersAt + 350),
      setTimeout(() => setCandleStage(2), candleEntersAt + 750),
      setTimeout(() => setCandleStage(3), candleEntersAt + 1300),
    ]
    return () => timers.forEach(clearTimeout)
  }, [mounted])

  return (
    <div className="w-full max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto px-6 py-10 sm:py-14 text-center select-none">
      <style>{`
        @keyframes synFlicker {
          0%, 100% { transform: translate(-50%, 0) scale(1, 1) rotate(-1deg); opacity: 0.95; }
          25%      { transform: translate(-50%, -1px) scale(1.04, 0.97) rotate(1.5deg); opacity: 1; }
          50%      { transform: translate(-50%, 0) scale(0.97, 1.05) rotate(-0.5deg); opacity: 0.92; }
          75%      { transform: translate(-50%, -2px) scale(1.02, 0.99) rotate(2deg); opacity: 1; }
        }
        @keyframes synHaloPulse {
          0%, 100% { opacity: 0.35; transform: translate(-50%, -50%) scale(1); }
          50%      { opacity: 0.55; transform: translate(-50%, -50%) scale(1.08); }
        }
        @keyframes synSpark {
          0%   { opacity: 0; transform: translate(-50%, 4px) scale(0.4); }
          50%  { opacity: 1; transform: translate(-50%, -2px) scale(1.1); }
          100% { opacity: 0; transform: translate(-50%, -10px) scale(0.6); }
        }
        @keyframes synIgnite {
          0%   { opacity: 0; transform: translate(-50%, 6px) scale(0.2, 0.2); }
          60%  { opacity: 1; transform: translate(-50%, -2px) scale(1.15, 1.25); }
          100% { opacity: 1; transform: translate(-50%, 0) scale(1, 1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .syn-flame { animation: none !important; }
          .syn-halo { animation: none !important; }
          .syn-spark { animation: none !important; opacity: 0 !important; }
        }
      `}</style>

      {/* Eyebrow "Day 1 of your streak" retired 2026-05-17 (Mason lock) —
          the lit candle + the streak count below carry the meaning without
          the explicit label. */}

      {/* Candle scene — CSS-painted from experimental, wrapped in
          professional's slot fade. */}
      <div
        className="relative h-56 sm:h-64 mx-auto mt-6"
        style={{ width: '120px', ...fadeStyle(SLOTS.candle, mounted) }}
        aria-hidden="true"
      >
        {/* Halo glow — fades in synced with flame ignition (stage 2), not after. */}
        <div
          className="syn-halo absolute pointer-events-none"
          style={{
            top: '38%',
            left: '50%',
            width: '320px',
            height: '320px',
            transform: 'translate(-50%, -50%)',
            background:
              'radial-gradient(circle, var(--gold) 0%, var(--gold-dim) 22%, transparent 62%)',
            opacity: candleStage >= 2 ? 0.45 : 0,
            transition: 'opacity 700ms ease-out',
            animation: candleStage >= 3 ? 'synHaloPulse 4.5s ease-in-out infinite' : 'none',
            filter: 'blur(20px)',
            zIndex: 0,
          }}
        />

        {/* Candle body */}
        <div
          className="absolute"
          style={{
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '34px',
            height: '140px',
            borderRadius: '6px 6px 3px 3px',
            background: 'linear-gradient(180deg, #f1e5c8 0%, #e6d4a8 35%, #c9a96b 100%)',
            boxShadow:
              'inset -6px 0 10px rgba(0,0,0,0.18), inset 6px 0 6px rgba(255,255,255,0.18), 0 6px 14px rgba(0,0,0,0.35)',
            zIndex: 2,
          }}
        >
          {/* drip */}
          <div
            style={{
              position: 'absolute',
              top: '30%',
              right: '-2px',
              width: '5px',
              height: '24px',
              background: 'linear-gradient(180deg, #e6d4a8, #c9a96b)',
              borderRadius: '0 0 4px 4px',
              boxShadow: 'inset -1px 0 2px rgba(0,0,0,0.2)',
            }}
          />
        </div>

        {/* Wick — darkens once the flame ignites */}
        <div
          className="absolute"
          style={{
            bottom: '140px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '2px',
            height: '12px',
            background: candleStage >= 2 ? '#1a1208' : '#2a1f10',
            transition: 'background 600ms',
            zIndex: 3,
          }}
        />

        {/* Spark — brief flash on wick before flame ignites */}
        <div
          className="syn-spark absolute pointer-events-none"
          style={{
            bottom: '146px',
            left: '50%',
            width: '14px',
            height: '14px',
            background: 'radial-gradient(circle, #fff7d6 0%, var(--gold) 45%, transparent 75%)',
            borderRadius: '50%',
            opacity: 0,
            animation: candleStage === 1 ? 'synSpark 500ms ease-out forwards' : 'none',
            zIndex: 4,
          }}
        />

        {/* Outer flame (orange/red base) — ignites with bounce, then flickers */}
        <div
          className="syn-flame absolute pointer-events-none"
          style={{
            bottom: '146px',
            left: '50%',
            width: '40px',
            height: '70px',
            borderRadius: '50% 50% 35% 35% / 70% 70% 30% 30%',
            background:
              'radial-gradient(ellipse at 50% 80%, #ffb347 0%, #ff7e2a 35%, #c8420f 70%, transparent 95%)',
            transformOrigin: '50% 100%',
            opacity: 0,
            transform: 'translate(-50%, 0)',
            animation:
              candleStage === 2
                ? 'synIgnite 700ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards'
                : candleStage >= 3
                  ? 'synFlicker 2.4s ease-in-out infinite'
                  : 'none',
            filter: 'blur(0.5px)',
            zIndex: 5,
            ...(candleStage >= 3 ? { opacity: 0.95 } : {}),
          }}
        />

        {/* Inner flame core (gold/white) — ignites with bounce 80ms after outer, then flickers reverse */}
        <div
          className="syn-flame absolute pointer-events-none"
          style={{
            bottom: '152px',
            left: '50%',
            width: '20px',
            height: '40px',
            borderRadius: '50% 50% 35% 35% / 70% 70% 30% 30%',
            background:
              'radial-gradient(ellipse at 50% 75%, #ffffff 0%, #fff2b3 30%, var(--gold) 65%, transparent 95%)',
            transformOrigin: '50% 100%',
            opacity: 0,
            transform: 'translate(-50%, 0)',
            animation:
              candleStage === 2
                ? 'synIgnite 700ms cubic-bezier(0.34, 1.56, 0.64, 1) 80ms forwards'
                : candleStage >= 3
                  ? 'synFlicker 1.8s ease-in-out infinite reverse'
                  : 'none',
            zIndex: 6,
            ...(candleStage >= 3 ? { opacity: 1 } : {}),
          }}
        />
      </div>

      {/* Heading */}
      <h1
        className="font-heading text-[24px] sm:text-[30px] leading-[1.2] text-[var(--text-primary)] mt-2 tracking-[-0.005em] px-2"
        style={fadeStyle(SLOTS.heading, mounted)}
      >
        You just sent your first message.
        <br />
        This is <span className="text-[var(--gold)]">Day 1</span> of your streak,{' '}
        <span className="font-semibold">{name}</span>.
      </h1>

      {/* Phil 1:6 — quoted left-rule */}
      <figure
        className="mt-8 mx-auto max-w-md border-l border-[var(--gold-dim)]/55 pl-5 text-left"
        style={fadeStyle(SLOTS.verse, mounted)}
      >
        <blockquote className="font-heading text-[15.5px] sm:text-[16px] leading-[1.65] text-[var(--text-muted)]">
          &ldquo;He who began a good work in you will bring it to completion at the day of Jesus
          Christ.&rdquo;
        </blockquote>
        <figcaption className="mt-2 text-[11px] uppercase tracking-[0.28em] text-[var(--text-muted-soft)]">
          Philippians 1:6
        </figcaption>
      </figure>

      {/* CTA — migrated to shared ContinueButton 2026-05-17 */}
      <div className="mt-12 flex justify-center" style={fadeStyle(SLOTS.cta, mounted)}>
        <ContinueButton onClick={onContinue}>Continue</ContinueButton>
      </div>
    </div>
  )
}
